/**
 * ComprarTodo e-commerce color system.
 * Change hex values here (and restart dev server) to retheme the storefront.
 * Tailwind classes use ct-primary, ct-price, ct-rating, ct-alert, ct-promo.
 */

const ECOMMERCE_PALETTE = {
    /** Green (emerald) — buy buttons, brand, trust */
    primary: {
        50: "#ecfdf5",  // very light green
        100: "#d1fae5", // light green
        200: "#a7f3d0", // pale green
        400: "#34d399", // medium green
        500: "#10b981", // green
        600: "#059669", // strong green (main CTA)
        700: "#047857", // dark green
        800: "#065f46", // deeper green
        900: "#064e3b", // very dark green
        950: "#022c22", // near-black green
    },
    /** Rose / pink-red — prices, hot deals, urgency */
    price: {
        50: "#fff1f2",  // very light pink
        100: "#ffe4e6", // light pink
        400: "#fb7185", // medium rose
        500: "#f43f5e", // rose / red-pink
        600: "#e11d48", // strong red-rose (price text)
    },
    /** Amber / gold — star ratings, social proof */
    rating: {
        500: "#f59e0b", // amber / gold
        600: "#d97706", // dark amber / orange-gold
    },
    /** Amber / yellow — login alerts, warnings */
    alert: {
        50: "#fffbeb",  // very light yellow
        200: "#fde68a", // light amber / yellow
        900: "#78350f", // dark brown-amber (alert text)
    },
    /** Teal / blue-green — promo gradients, accents */
    promo: {
        400: "#2dd4bf", // light teal
        500: "#14b8a6", // teal / blue-green
    },
};

/** Semantic Tailwind class bundles — import EC in components. */
const EC = {
    page: {
        root: "min-h-screen bg-slate-50 text-slate-900 antialiased selection:bg-ct-primary-200 selection:text-ct-primary-950",
        blobPrimary: "bg-ct-primary-400/15",
        blobPrice: "bg-ct-price-400/10",
    },

    brand: {
        text: "text-ct-primary-700 hover:text-ct-primary-800",
        textLg: "font-extrabold text-ct-primary-700 hover:text-ct-primary-800",
    },

    nav: {
        active: "text-ct-primary-700",
        link: "text-slate-600 hover:text-ct-primary-700",
        langHover: "hover:bg-ct-primary-50 hover:border-ct-primary-100",
        langActive: "bg-ct-primary-50 text-ct-primary-800",
    },

    hero: {
        badge: "bg-ct-primary-100 text-ct-primary-800 border border-ct-primary-200/60",
        titleGradient: "text-transparent bg-clip-text bg-gradient-to-r from-ct-primary-600 via-ct-promo-500 to-ct-primary-600",
        statValue: "text-ct-price-600",
        imageRing: "shadow-ct-primary-900/10 ring-1 ring-ct-primary-100",
        statCard: "border-ct-primary-100",
    },

    btn: {
        primary:
            "bg-ct-primary-600 text-white hover:bg-ct-primary-700 shadow-lg shadow-ct-primary-600/30 transition font-semibold",
        primaryMd:
            "bg-ct-primary-600 text-white px-3 sm:px-5 py-2 rounded-xl hover:bg-ct-primary-700 transition font-semibold text-sm shadow-md shadow-ct-primary-600/20",
        primaryLg:
            "inline-flex justify-center items-center gap-2 bg-ct-primary-600 text-white px-7 py-3.5 rounded-xl text-base font-semibold hover:bg-ct-primary-700 transition shadow-lg shadow-ct-primary-600/30",
        secondary:
            "inline-flex justify-center items-center gap-2 bg-white text-slate-800 px-7 py-3.5 rounded-xl text-base font-semibold border-2 border-slate-200 hover:border-ct-primary-400 hover:text-ct-primary-800 transition",
        ghost: "text-ct-primary-700 px-5 py-3.5 rounded-xl text-base font-semibold hover:bg-ct-primary-50 transition",
        outline:
            "rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-ct-primary-300 hover:bg-ct-primary-50 hover:text-ct-primary-800",
        addToCart:
            "inline-flex items-center justify-center gap-2 rounded-xl bg-ct-primary-600 font-semibold text-white shadow-lg shadow-ct-primary-600/25 transition hover:bg-ct-primary-700 active:scale-[0.98]",
        share:
            "inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white text-slate-700 hover:border-ct-primary-300 hover:bg-ct-primary-50 hover:text-ct-primary-700 transition font-medium",
        shareMenuItem: "hover:bg-ct-primary-50 hover:text-ct-primary-800",
    },

    price: {
        text: "text-ct-price-600",
        textLg: "text-3xl sm:text-4xl font-extrabold text-ct-price-600",
        textXl: "text-xl font-extrabold text-ct-price-600 sm:text-2xl",
        box: "rounded-2xl border border-ct-price-100 bg-ct-price-50/60 px-4 py-3",
        label: "text-xs font-semibold uppercase tracking-wide text-ct-price-500",
        pill: "text-sm font-medium text-ct-price-600 bg-ct-price-50 px-3 py-1.5 rounded-full",
    },

    badge: {
        new: "bg-ct-primary-500",
        hot: "bg-ct-price-500",
        hotPulse: "bg-ct-price-500 animate-pulse",
        sale: "bg-ct-price-500",
    },

    rating: {
        text: "text-ct-rating-500",
        textSm: "flex shrink-0 items-center gap-1 text-xs font-semibold text-ct-rating-500",
    },

    alert: {
        box: "rounded-xl border border-ct-alert-200 bg-ct-alert-50 px-4 py-3 text-sm text-ct-alert-900",
        loginHint:
            "absolute -top-14 left-1/2 z-20 w-max max-w-[16rem] -translate-x-1/2 rounded-xl border border-ct-alert-200 bg-ct-alert-50 px-3 py-2 text-center text-xs font-medium text-ct-alert-900 shadow-lg sm:text-sm",
    },

    chip: {
        category: "rounded-full bg-ct-primary-50 px-2.5 py-0.5 text-xs font-medium text-ct-primary-700",
        categoryLg: "rounded-full bg-ct-primary-100 px-3 py-1 text-sm font-semibold text-ct-primary-800",
    },

    category: {
        active: "border-ct-primary-400 bg-ct-primary-50 shadow-md shadow-ct-primary-200/50",
        inactive: "border-slate-200 bg-white hover:border-ct-primary-300 hover:bg-ct-primary-50/50",
        labelActive: "text-ct-primary-800",
        labelInactive: "text-slate-700 group-hover:text-ct-primary-700",
        scrollBtn: "hover:border-ct-primary-300 hover:text-ct-primary-600",
    },

    card: {
        base: "rounded-2xl border border-slate-200 bg-white shadow-sm",
        hover: "hover:-translate-y-1 hover:border-ct-primary-200 hover:shadow-xl hover:shadow-ct-primary-900/5 transition",
        titleHover: "group-hover:text-ct-primary-800",
    },

    gallery: {
        dotActive: "h-2 w-6 rounded-full bg-ct-primary-500",
        dotInactive: "h-2 w-2 rounded-full bg-white/80",
        thumbActive: "border-ct-primary-500 ring-2 ring-ct-primary-200",
        thumbInactive: "border-slate-200 hover:border-ct-primary-300",
    },

    cart: {
        badge: "rounded-xl border border-ct-primary-200 bg-ct-primary-50 px-3 py-2 text-sm font-semibold text-ct-primary-800",
        toast: "rounded-lg bg-ct-primary-700 px-3 py-1.5 text-xs font-medium text-white shadow-lg",
    },

    section: {
        promo: "bg-gradient-to-br from-ct-primary-700 via-ct-primary-600 to-ct-promo-500 text-white",
        promoSubtitle: "text-ct-primary-50/90",
        promoCode: "text-ct-primary-100/80",
    },

    footer: {
        whatsapp: "bg-ct-primary-600 text-white font-semibold hover:bg-ct-primary-500",
    },

    fab: {
        whatsapp:
            "bg-ct-primary-500 text-white shadow-lg shadow-ct-primary-900/30 hover:bg-ct-primary-400 hover:scale-105 transition",
    },

    icon: {
        primary: "text-ct-primary-600",
    },

    form: {
        card: "relative rounded-3xl border border-slate-200/80 bg-white p-6 sm:p-9 shadow-xl shadow-slate-900/5",
        cardGlow:
            "absolute inset-0 rounded-3xl bg-gradient-to-br from-ct-primary-500/5 to-ct-promo-500/10 pointer-events-none",
        label: "block text-sm font-semibold text-slate-800 mb-1.5",
        input:
            "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-ct-primary-500 focus:ring-2 focus:ring-ct-primary-500/20 outline-none transition text-base disabled:opacity-60 disabled:cursor-not-allowed",
        helper: "text-xs text-slate-500 flex justify-end mt-1",
        error: "text-sm text-ct-price-600 mt-2",
        link: "inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-ct-primary-700 font-medium transition",
        submit:
            "w-full inline-flex justify-center items-center gap-2 bg-ct-primary-600 text-white px-8 py-3.5 rounded-xl text-base font-semibold hover:bg-ct-primary-700 transition shadow-lg shadow-ct-primary-600/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-ct-primary-600",
        stepActive: "bg-ct-primary-600 text-white",
        stepInactive: "bg-slate-200 text-slate-600",
        stepDone: "bg-ct-primary-100 text-ct-primary-800",
    },
};

module.exports = {
    ECOMMERCE_PALETTE,
    EC,
};
